import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Loader2, Mail, RefreshCw, Send, Settings, TrendingUp, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact card for the Financials tab that lets the owner preview the weekly
 * digest KPIs (MRR, revenue, signups, security), manage recipient emails,
 * and manually trigger a send-now (optionally with a CSV attachment).
 *
 * Wraps three backend endpoints:
 *   GET  /api/admin/owner-digest/preview?window_days=7
 *   POST /api/admin/owner-digest/send?attach_csv=<bool>
 *   GET/PUT /api/admin/owner-digest/settings  (recipients + attach_csv)
 */
export default function AdminOwnerDigestCard({ token }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastSend, setLastSend] = useState(null);

  // Settings state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null); // {recipients, attach_csv, resolved_recipients, env_recipients}
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/owner-digest/preview?window_days=7`, { headers: authHeaders });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Preview failed');
      setMetrics(data);
    } catch (e) {
      toast.error(e.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const r = await fetch(`${API}/admin/owner-digest/settings`, { headers: authHeaders });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Settings load failed');
      setSettings(data);
    } catch (e) {
      toast.error(e.message || 'Settings load failed');
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    if (settingsOpen && !settings) fetchSettings();
  }, [settingsOpen]);

  const saveSettings = async (patch = {}) => {
    if (!settings) return;
    const next = {
      recipients: patch.recipients ?? settings.recipients ?? [],
      attach_csv: patch.attach_csv ?? settings.attach_csv ?? false,
    };
    setSavingSettings(true);
    try {
      const r = await fetch(`${API}/admin/owner-digest/settings`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Save failed');
      setSettings((prev) => ({ ...prev, ...data }));
      toast.success('Digest settings saved');
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSavingSettings(false);
    }
  };

  const addRecipient = async () => {
    const e = newEmail.trim();
    if (!e || !e.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    const existing = settings?.recipients || [];
    if (existing.some((x) => x.toLowerCase() === e.toLowerCase())) {
      toast.info('Already in the list');
      return;
    }
    await saveSettings({ recipients: [...existing, e] });
    setNewEmail('');
  };

  const removeRecipient = async (email) => {
    const existing = settings?.recipients || [];
    await saveSettings({ recipients: existing.filter((x) => x !== email) });
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await fetch(`${API}/admin/owner-digest/send`, {
        method: 'POST', headers: authHeaders,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Send failed');
      setLastSend(data);
      const ok = data.ok || (data.sends || []).filter((s) => s.ok).length > 0;
      const okCount = (data.sends || []).filter((s) => s.ok).length;
      const total = (data.recipients || []).length;
      if (ok) toast.success(`Digest delivered → ${okCount}/${total} recipients (week ${data.week})`);
      else if (data.skipped) toast.info(`Skipped: ${data.skipped}`);
      else toast.warning('Sent but no delivery succeeded — check logs');
    } catch (e) {
      toast.error(e.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="owner-digest-card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-400" />
              Weekly Owner Digest
            </CardTitle>
            <CardDescription>
              KPI snapshot for the last 7 days. Cron ships every Monday 08:00 UTC.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSettingsOpen((v) => !v)}
              data-testid="owner-digest-settings-btn"
              title="Manage recipients"
            >
              <Settings className="w-4 h-4 mr-1" />
              {settingsOpen ? 'Hide settings' : 'Recipients'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPreview}
              disabled={loading}
              data-testid="owner-digest-preview-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Preview
            </Button>
            <Button
              size="sm"
              onClick={sendNow}
              disabled={sending}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="owner-digest-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send now
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Recipients editor */}
        {settingsOpen && (
          <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-4 space-y-3" data-testid="owner-digest-settings-panel">
            {settingsLoading || !settings ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading recipients…
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-400">Env-baked (always included)</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(settings.env_recipients || []).length ? (
                      settings.env_recipients.map((e) => (
                        <Badge key={e} variant="secondary" className="bg-slate-700 text-slate-200">{e}</Badge>
                      ))
                    ) : (
                      <span className="text-xs text-slate-500">None. Set REVENUE_NOTIFY_EMAIL in backend .env.</span>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-400">Extra recipients</Label>
                  <div className="flex flex-col gap-1 mt-1" data-testid="owner-digest-recipients-list">
                    {(settings.recipients || []).map((e) => (
                      <div key={e} className="flex items-center justify-between bg-slate-900 rounded px-2 py-1">
                        <span className="text-sm text-slate-200">{e}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeRecipient(e)}
                          disabled={savingSettings}
                          data-testid={`remove-recipient-${e}`}
                          className="text-red-400 hover:text-red-300 h-7 px-2"
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                    {(settings.recipients || []).length === 0 && (
                      <span className="text-xs text-slate-500">No extras. Add co-founders / accountant below.</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      type="email"
                      placeholder="cofounder@example.com"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }}
                      disabled={savingSettings}
                      data-testid="owner-digest-add-recipient-input"
                      className="bg-slate-900 border-slate-700 text-sm"
                    />
                    <Button
                      size="sm"
                      onClick={addRecipient}
                      disabled={savingSettings || !newEmail.trim()}
                      data-testid="owner-digest-add-recipient-btn"
                    >
                      Add
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-slate-700">
                  <div>
                    <Label className="text-sm text-slate-200">Attach transactions CSV</Label>
                    <p className="text-xs text-slate-500">Board-report ready — appends the week&apos;s paid txns as a CSV file.</p>
                  </div>
                  <Switch
                    checked={!!settings.attach_csv}
                    onCheckedChange={(v) => saveSettings({ attach_csv: v })}
                    disabled={savingSettings}
                    data-testid="owner-digest-attach-csv-toggle"
                  />
                </div>

                <p className="text-xs text-slate-500 pt-1">
                  Resolved this week → <b>{(settings.resolved_recipients || []).length}</b> recipient(s).
                </p>
              </>
            )}
          </div>
        )}

        {/* Metrics preview */}
        {!metrics && !loading && (
          <div className="text-center py-6 text-slate-400" data-testid="owner-digest-empty">
            <TrendingUp className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Click <b>Preview</b> to load this week&apos;s KPIs, or <b>Send now</b> to email the digest.</p>
          </div>
        )}
        {metrics && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="owner-digest-metrics">
            <MetricTile label="MRR (est.)" value={money(metrics.mrr?.estimated_usd)} sub={`${metrics.mrr?.active_subs_total || 0} active subs`} accent="text-emerald-400" />
            <MetricTile label="Revenue (7d)" value={money(metrics.revenue?.window_amount)} sub={`${metrics.revenue?.window_txn_count || 0} txns`} accent="text-green-400" />
            <MetricTile label="Signups" value={`+${metrics.users?.new || 0}`} sub={`${metrics.users?.total || 0} total users`} accent="text-blue-400" />
            <MetricTile label="New members" value={String(metrics.communities?.new_members || 0)} sub={`${metrics.communities?.total || 0} communities`} accent="text-purple-400" />
            <MetricTile label="New projects" value={String(metrics.projects?.new || 0)} sub={`${metrics.projects?.total || 0} total`} accent="text-amber-400" />
            <MetricTile label="Failed logins" value={String(metrics.security?.failed_logins || 0)} sub={`${(metrics.security?.top_offender_ips || []).length} unique IPs`} accent="text-red-400" />
          </div>
        )}

        {/* Last send summary */}
        {lastSend && (
          <div className="rounded-md bg-slate-800/60 border border-slate-700 p-3 text-xs text-slate-300" data-testid="owner-digest-last-send">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold">Last send:</span>
              {lastSend.week && <span>week <code>{lastSend.week}</code></span>}
              {lastSend.recipients && (
                <span>· <b>{(lastSend.sends || []).filter((s) => s.ok).length}</b>/{lastSend.recipients.length} delivered</span>
              )}
              {lastSend.attach_csv && <Badge variant="secondary" className="bg-emerald-900/50 text-emerald-300 text-[10px]">CSV attached</Badge>}
              {lastSend.skipped && <span className="text-amber-400">skipped: {lastSend.skipped}</span>}
            </div>
            {(lastSend.sends || []).length > 0 && (
              <div className="mt-2 space-y-1 max-h-32 overflow-auto">
                {lastSend.sends.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-[11px]">
                    <span className="text-slate-400">{s.recipient}</span>
                    <span className={s.ok ? 'text-emerald-400' : s.skipped ? 'text-slate-500' : 'text-amber-400'}>
                      {s.ok ? '✓ delivered' : s.skipped || s.reason || 'failed'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MetricTile({ label, value, sub, accent = 'text-white' }) {
  return (
    <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-4">
      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${accent}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
